import React from 'react';
import styled from 'styled-components';
import { Box, InputAdornment, Typography } from '@material-ui/core';
import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { TextField } from 'common/components/TextField';
import { FormField } from 'common/components/FormField';
import { Link } from 'common/components/Link';
import { getIntlPath } from 'common/utils';
import { Button } from 'common/components/buttons/Button';

const LinkContainer = styled.div``;

export type TLoginFormValues = {
    email: string;
    password: string;
};

interface IProps {
    className?: string;
    loading: boolean;
    onSubmit(values: TLoginFormValues): void;
}

const BaseLoginForm: React.FC<IProps> = ({ className, loading, onSubmit }) => {
    const { t } = useTranslation();

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email(t('validators.email.invalid'))
                .required('validators.email.required'),
            password: Yup.string().required(
                t('validators.password.requiredLogin')
            ),
        }),
        onSubmit: onSubmit,
    });

    const {
        errors: formikErrors,
        touched,
        values,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TLoginFormValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <form className={className} onSubmit={handleSubmit}>
            <Typography color="primary" variant="h2">
                {t('account.login.title')}
            </Typography>
            <Box className="description">
                <Typography color="textSecondary" variant="body2">
                    {t('account.login.description')}
                </Typography>
            </Box>
            <FormField>
                <TextField
                    error={getFieldError('email')}
                    id="email"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                disablePointerEvents
                                position="start"
                            >
                                <MailOutlineOutlinedIcon fontSize="default" />
                            </InputAdornment>
                        ),
                    }}
                    label="e-mail"
                    type="email"
                    value={values.email}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>
            <FormField>
                <TextField
                    error={getFieldError('password')}
                    id="password"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                disablePointerEvents
                                position="start"
                            >
                                <LockOutlinedIcon fontSize="default" />
                            </InputAdornment>
                        ),
                    }}
                    label={t('formfields.password')}
                    type="password"
                    value={values.password}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>
            <Box textAlign="center">
                <Link to={getIntlPath('forgotPassword')}>
                    {t('account.login.linkForgotPassword')}
                </Link>
            </Box>
            <Box mb={7} mt={7}>
                <Button fullWidth loading={loading} type="submit">
                    {t('account.login.submit')}
                </Button>
            </Box>
            <LinkContainer>
                <div>
                    {t('account.login.customerService')}:{' '}
                    <Link
                        href={`mailto:${t(
                            'account.login.customerServiceEmail'
                        )}`}
                        target="_blank"
                        to=""
                    >
                        {t('account.login.customerServiceEmail')}
                    </Link>{' '}
                </div>
                <Link
                    href={t('account.login.generalTermsUrl')}
                    target="_blank"
                    to=""
                >
                    {t('account.login.generalTerms')}
                </Link>{' '}
                |{' '}
                <Link
                    href={t('account.login.privacyUrl')}
                    target="_blank"
                    to=""
                >
                    {t('account.login.privacy')}
                </Link>{' '}
                |{' '}
                <Link
                    href={t('account.login.cookiesUrl')}
                    target="_blank"
                    to=""
                >
                    {t('account.login.cookies')}
                </Link>
            </LinkContainer>
        </form>
    );
};

export const LoginForm = styled(BaseLoginForm)`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .description {
        margin-bottom: ${({ theme }) => `${theme.spacing(10)}px`};
    }

    ${LinkContainer} {
        margin-top: auto;
        font-size: ${({ theme }) => theme.typography.body1.fontSize};
        color: ${({ theme }) => theme.palette.primary.main};
        line-height: 2;

        ${Link} {
            margin-top: 4px;
        }
    }
`;
